// HOW THINGS ARRIVE AND LEAVE, in one file.
//
// This product had no motion at all, and not because CSS was insufficient. The six
// generated overlays carry seventeen `animate-in` / `fade-in-0` / `zoom-in-95` classes
// from `tw-animate-css` — a package that is not in `package.json`. Measured in the
// running app, the served stylesheet's only keyframes were `pulse`, `spin` and sonner's
// own, and every overlay reported `animationName: "none"`. What was left behind was a
// bare `duration-100`, which sets a duration on `transition-property: all` — the wrong
// property list on the weakest curve CSS has.
//
// # A motion library, at ONE surface, and that is a decision with a threshold
//
// This section read *"No motion library"* until 2026-08-17 and the reversal is recorded
// here rather than rewritten away, because the argument it was built on is still the
// argument — only its conclusion moved.
//
// What it said: `motion` (ex-Framer Motion) was installed and imported by nothing, and
// every moment this product has — an overlay entering, a scrim, a disclosure, a row
// appearing, a toast — is covered by a CSS transition driven by Base UI's
// `data-starting-style` / `data-ending-style`, which already holds the element mounted for
// its exit. A library would be a second animation system beside the CSS one Base UI,
// sonner and cmdk already use, with no shared timing source.
//
// **Every clause of that is still true, and the last one is what changed.** The timing
// source is now shared: `ui/motion-runtime.ts` reads the same generated custom properties
// these strings name, at animation time, so the library and the classes are two engines
// over ONE set of tokens rather than two systems. Without that file the old objection
// stands unanswered, which is why `check-tokens.mjs` refuses a `motion` import that does
// not reach it.
//
// The premise for adopting it is the product owner's and is a fact about the engine rather
// than a preference: `motion` drives the Web Animations API, so an opacity or a transform
// is handed to the compositor exactly as a CSS transition is — measured in Chromium, the
// pane's entrance is a native `Animation` with `opacity` keyframes — while the sequencing
// stays in code where it can be composed, orchestrated and interrupted.
//
// IT IS ONE SURFACE AND THAT IS THE THRESHOLD. `ui/sheet.tsx` and nothing else. A moment
// covered by a CSS transition today has no reason to move, and the honest reading of the
// measurement below is that the transitions were never the defect — a pane's LATENCY and a
// pane's TRANSITION look identical from a chair and are not the same thing.
//
// The old note said to revisit this the day the POINTER DRIVES POSITION CONTINUOUSLY and a
// release must carry momentum while staying interruptible — drag-to-reorder in the org
// chart, swipe-to-triage in the inbox. That is still the case the library is uniquely
// needed for, and it is still unbuilt: velocity retention across an interruption is the one
// thing no CSS curve does, since a cancelled transition re-targets from its current value
// but restarts at ZERO velocity, so it visibly stalls.
//
// # TRANSITIONS, not keyframes
//
// `tw-animate-css` was rejected too, and that is the less obvious call: it is CSS-only,
// zero-runtime, and installing it would light up all seventeen dead classes at once.
// But it lights them up as KEYFRAMES, and a keyframe restarts from frame zero when
// re-triggered — a manager double-clicking a leave row would see the panel snap. A
// transition retargets from wherever it currently is. Base UI's own handbook says the
// same thing, and the sheet in this repo already demonstrates it working.
//
// # Every value is a token, and reduced motion is not a branch
//
// No duration and no curve is written here. Under `prefers-reduced-motion: reduce` the
// generated stylesheet RE-POINTS `--motion-travel-*` to `0rem` and `--motion-scale-*`
// to `1`, so these strings do the right thing without knowing the setting exists. A
// component that had to remember to check is a component that forgets.

/**
 * A dialog and an alert dialog.
 *
 * `transform-origin` stays at the centre, unlike a popover: a dialog is not spatially
 * owned by whatever opened it, and the palette's trigger is a keystroke with no
 * position on screen at all.
 */
export const overlayMotion =
  "transition-[opacity,scale] duration-(--motion-duration-overlay-enter) ease-(--motion-ease-enter) " +
  "data-starting-style:opacity-0 data-starting-style:scale-(--motion-scale-overlay) " +
  "data-ending-style:opacity-0 data-ending-style:scale-(--motion-scale-overlay) " +
  "data-ending-style:duration-(--motion-duration-overlay-exit) data-ending-style:ease-(--motion-ease-exit)";

/**
 * The scrim under a dialog.
 *
 * It borrows the panel's duration and has none of its own, so the two cannot drift
 * apart. They already had: the sheet's scrim ran at 150ms against a 200ms panel, so the
 * page finished dimming before anything arrived.
 *
 * Opacity only. `backdrop-filter` is set and never animated — on software raster the
 * same scrim went from 16.7ms to 30.4ms per frame when it was.
 */
export const overlayScrim =
  "transition-opacity duration-(--motion-duration-overlay-enter) ease-(--motion-ease-enter) " +
  "data-starting-style:opacity-0 data-ending-style:opacity-0 " +
  "data-ending-style:duration-(--motion-duration-overlay-exit) data-ending-style:ease-(--motion-ease-exit)";

/**
 * A dropdown, a select, a menu — anchored to a trigger and opened many times a sitting.
 *
 * Short on purpose. Past roughly 150ms a person stops reading an anchored menu's
 * entrance as an effect and starts reading it as the system being slow.
 */
export const menuMotion =
  "origin-(--transform-origin) transition-[opacity,scale] " +
  "duration-(--motion-duration-menu-enter) ease-(--motion-ease-enter) " +
  "data-starting-style:opacity-0 data-starting-style:scale-(--motion-scale-menu) " +
  "data-ending-style:opacity-0 data-ending-style:scale-(--motion-scale-menu) " +
  "data-ending-style:duration-(--motion-duration-menu-exit) data-ending-style:ease-(--motion-ease-exit)";

/**
 * A popover and a hover card. Slower than a menu, and from the trigger.
 *
 * A popover carries something to read rather than a list to click through, so it earns
 * the extra 40ms. It scales from `--transform-origin`, which Base UI sets on the
 * positioner from the trigger's real geometry — a popover that grew from the centre of
 * itself would come from nowhere in particular.
 */
export const popoverMotion =
  "origin-(--transform-origin) transition-[opacity,scale] " +
  "duration-(--motion-duration-popover-enter) ease-(--motion-ease-enter) " +
  "data-starting-style:opacity-0 data-starting-style:scale-(--motion-scale-menu) " +
  "data-ending-style:opacity-0 data-ending-style:scale-(--motion-scale-menu) " +
  "data-ending-style:duration-(--motion-duration-popover-exit) data-ending-style:ease-(--motion-ease-exit)";

/**
 * A tooltip.
 *
 * `data-instant` is Base UI's own signal that another tooltip was already open when
 * this one was asked for. The person has demonstrated their intent by crossing the
 * toolbar, so repeating the delay and the fade makes the whole strip feel sluggish —
 * the second tooltip appears immediately, with no transition at all.
 */
export const tooltipMotion =
  "origin-(--transform-origin) transition-[opacity,scale] " +
  "duration-(--motion-duration-tooltip-enter) ease-(--motion-ease-enter) " +
  "data-starting-style:opacity-0 data-starting-style:scale-(--motion-scale-tooltip) " +
  "data-ending-style:opacity-0 data-ending-style:scale-(--motion-scale-tooltip) " +
  "data-ending-style:duration-(--motion-duration-tooltip-exit) data-ending-style:ease-(--motion-ease-exit) " +
  "data-instant:transition-none";

/**
 * A side panel, and the only thing here that travels.
 *
 * `--motion-travel-panel` is 2.5rem and never the panel's width. A full-width slide is
 * the area-scaled movement that is an actual vestibular trigger; forty pixels is a hint
 * about where the thing came from. Under reduced motion the token is `0rem` and the
 * panel cross-fades in place over the same duration.
 *
 * A `translate-x-full` is the idiomatic drawer move — it is what Vaul animates
 * (`@keyframes slideFromRight { from { transform: translate3d(100%, 0, 0) } }`) — and it is
 * refused here for the reason `distance.panel` carries in the token file, and for a second
 * one that decides it: a percentage translate CANNOT BE RE-POINTED TO `0rem`, so the one
 * reader who asked for less movement would be handed the largest movement in the product.
 * `moduleNavMotion` refuses it on the same two grounds. Said here as well because "make the
 * pane slide in from the edge like everybody else's" is the obvious later change.
 *
 * # THE EXIT AND THE ENTRANCE ARE ONE CURVE NOW
 *
 * `--motion-ease-exit` was `ease.in` until 2026-08-09 — flat for its first fifth, so a pane
 * dismissed with Escape spent the opening frames of its exit not moving. Measured on the
 * running stack rather than read off the cubic-bezier: the curve is unchanged in shape and
 * the panel now leaves on `ease.out`, the same curve it arrives on, over the shorter exit
 * duration. Six other declarations in this file took the same correction from the same one
 * line, which is the point of its being a token.
 *
 * WHAT THIS TRANSITION IS NOT RESPONSIBLE FOR, measured by `tests/e2e/pane-motion.mjs`:
 * the delay between the press and the first moving frame. At 4x CPU throttling that is
 * 263ms opening and 288ms closing, and a V8 profile puts all of it in ONE synchronous
 * `ApplicationShell` render (`workLoopSync` → `ApplicationShell`,
 * `src/app/application-shell.tsx`) that runs before the browser
 * can paint. The transition itself is clean on both sides of it — Chrome's own trace reports
 * `compositeFailed: 0`, and 21–22 eased frames are painted with no gap over 7ms. A slow
 * pane and a janky transition look identical from a chair and are not the same defect.
 *
 * # AND THE PANEL'S OWN ENTRANCE AND EXIT ARE `motion`'S NOW, WHILE THIS IS NOT
 *
 * The pane is the one surface driven by the animation library rather than by a class, and
 * the split is deliberate rather than partial. `motion` owns `opacity` and `transform` —
 * the entrance and the exit, interruptible, retargetable, and readable by Base UI through
 * `getAnimations()` so a pane is never unmounted mid-exit. What stays here is the
 * UNDERLAP: the resting offset a sheet takes when another one opens on top of it, which is
 * not an entrance and not an exit but a move between two places the panel STAYS at.
 *
 * They are separate CSS properties, which is what makes the split free rather than a
 * negotiation: Tailwind's `translate-x-*` compiles to the `translate` property and
 * `motion` writes `transform`, and the two compose by specification (`translate`, then
 * `rotate`, then `scale`, then `transform`). So the panel can be travelling under
 * `motion`'s hand and offset under this one at the same time, with neither overwriting the
 * other — which a single `transform` owned by both could not do.
 *
 * It is `transition-[translate]` and NEVER `transition-all`: a shorthand here would take
 * `transform` back off `motion` mid-animation, which is the one way this split can break.
 */
export const panelUnderlap =
  "transition-[translate] duration-(--motion-duration-panel-enter) ease-(--motion-ease-enter)";

/**
 * THE MODULE SIDEBAR OPENING AND CLOSING, and the one moment here that was measured
 * being got wrong before it was got right.
 *
 * # What it used to do, in numbers
 *
 * `transition-[width,border-color]`. The sidebar sits in a GRID TRACK, and that track is
 * what sizes the content column beside it — so animating its width re-laid out `main` on
 * every frame of the transition. Measured on `/leave`, sampled per painted frame:
 * hiding walked through **10 distinct painted states over 216ms**, showing through 11
 * over 200ms, and **the table's own columns were recomputed on 9 and 10 of them** — a
 * first cell going 403 → 410 → 426 → 442 → 457 → 469 → 478 → 484 → 488 → 491px. That is
 * the auto table-layout algorithm running once per frame, and the text under it re-wrapped
 * with it. The product owner saw exactly that and described it from the outside: *the
 * letters compress as it slides, and then suddenly stretch all the way down.* A paragraph
 * gaining a line mid-animation is what "stretches down" is.
 *
 * It also bought nothing. Ten layout-shift entries totalling **0.1233**, against **0.1328**
 * for the instant version — the animation did not reduce the movement, it spread the same
 * movement across ten re-layouts.
 *
 * # So the WIDTH is not animated at all, and the panel is
 *
 * The track's width changes in one step and the sidebar — taken out of flow, so the track
 * cannot squeeze it — travels and fades. `translate` and `opacity` are the two properties
 * that skip layout entirely, which is the rule the rest of this file already keeps and
 * which this was the product's largest violation of.
 *
 * **There is exactly one re-layout left and it is at the START**, on the frame the button
 * is pressed. That is unavoidable rather than tolerated: the content genuinely occupies
 * more room afterwards, so its text must re-wrap once, and no compositor-only property can
 * express a real change of available width. Putting it at the start is the choice — a
 * re-wrap at the END lands at the instant the movement stops and the eye arrives to read,
 * while one at the start is simultaneous with the press and reads as the consequence of
 * it. Everything after that frame is stable: the column's width, its wrapping and its
 * height do not move again.
 *
 * # It travels `--motion-travel-panel`, and NEVER the sidebar's own width
 *
 * `design/tokens/primitives.json` writes the rule down on the token itself — "NEVER its
 * full width; a full-width slide is the area-scaled motion that is an actual vestibular
 * trigger" — so this is 2.5rem and a fade, exactly like `panelMotion`, rather than a
 * 224px journey. Under `prefers-reduced-motion` that token is re-pointed to zero and the
 * panel cross-fades in place, with no component branching on the setting.
 *
 * # AND THE EXIT WAS THE ONE DIRECTION THIS NEVER ACTUALLY ANIMATED
 *
 * The product owner: *"sidebar çok güzel bir animasyon ile açılıyor ama kapanırken tak
 * diye gidiyor, bu çok çirkin"* — it opens beautifully and it goes with a bang. Sampled
 * per PAINTED FRAME on `/leave` by `tests/e2e/rail-motion.mjs`, which records the panel's
 * own viewport geometry rather than only its computed style:
 *
 *     +  0ms  panel[  56 .. 280]  track=224  content.left=280  opacity=1.00
 *     + 42ms  panel[-168 ..  56]  track=  0  content.left= 56  opacity=1.00   <-- one frame
 *     + 57ms  panel[-174 ..  50]  track=  0  content.left= 56  opacity=0.86
 *     +157ms  panel[-208 ..  16]  track=  0  content.left= 56  opacity=0.00
 *
 * **ONE FRAME, 224 PIXELS, AT FULL OPACITY.** The fade and the travel under it were real,
 * eased and measurable — and every frame of them ran past the icon rail and off-canvas,
 * because the panel had already teleported there before the first was painted. What a
 * reader sees is the sidebar ceasing to exist.
 *
 * The transition was never the fault, and no amount of retiming it would have helped:
 * the panel is `absolute … right-0` inside the TRACK, and the track's width went to zero
 * on the frame of the press. **Its POSITION is layout, and layout is the one thing here
 * that was deliberately not animating.** Opening hid the identical 224px jump perfectly,
 * because there it happens at `opacity: 0` before the entrance begins — which is how one
 * direction read as considered and the other as broken while both carried this same
 * declaration. A computed style is not evidence about a moving element; where it IS is.
 *
 * # SO THE SPACE IS HELD UNTIL THE PANEL HAS GONE
 *
 * Three shapes are available and two of them are refused.
 *
 * The track cannot collapse at the press and let the panel fade in place: the content has
 * taken that space by the next frame, so the panel spends its whole exit drawn over the
 * page title and the table's first column. That is the ghosting the RIGHT anchor exists
 * to prevent, arriving from the other side.
 *
 * The panel cannot travel its own width out of the way, which is the idiomatic drawer
 * move and is refused twice: `distance.panel` carries "NEVER its full width" in the token
 * file, and — the half that actually decides it — a `-translate-x-full` cannot be
 * re-pointed to `0rem`, so the one reader who asked for LESS movement would be handed the
 * largest movement in the product. That is the defect inverted, not fixed.
 *
 * What is left is the shape every exit animation has: KEEP THE THING FOR THE DURATION OF
 * ITS EXIT, THEN REMOVE IT. What is kept here is not the element — that never unmounted —
 * it is the SPACE. The track's width is a constant now and its `display` is what changes,
 * transitioned as a discrete property with `allow-discrete`, which the browser resolves
 * asymmetrically BY SPECIFICATION: arriving from `none` it applies at the START, going to
 * `none` it applies at the END. One declaration, both directions, and the asymmetry that
 * was hand-written and wrong is the browser's own.
 *
 * `display` is not `width`. It does not interpolate, so the content column is still
 * re-laid out EXACTLY ONCE and every claim in the section above survives. What moved is
 * WHEN — from the frame of the press to the frame the panel finishes leaving. That is a
 * real trade against the argument above, which says the re-wrap belongs at the start, and
 * it is taken deliberately: that argument is about a panel with somewhere to go, and this
 * one has nowhere to go until the space it stands in is still there. A single reflow
 * somebody can see costs less than an exit nobody can.
 */
export const moduleNavMotion =
  "transition-[translate,opacity] duration-(--motion-duration-disclosure-open) ease-(--motion-ease-move)";

/**
 * The same, closing. Faster, like every exit here — and the `searchMotion` pair's shape.
 *
 * ONE CURVE FOR BOTH DIRECTIONS, and it is `--motion-ease-move` because a control somebody
 * toggles both ways moves between two RESTING POSITIONS: the direction is the difference,
 * not the feel of it. That is still the reason, and it is now the only one.
 *
 * It used to be two reasons, and the second was a workaround. This comment read *"it stays
 * `--motion-ease-move` rather than becoming `--motion-ease-exit`. That token is `ease.in`:
 * it starts slow, which spends the first frames — the exact moment the eye is on the thing
 * it just pressed — going nowhere"* — which was TRUE, and was a defect in the TOKEN being
 * routed around in one call site. Every other exit in the product went on starting slow.
 * `motion.ease.exit` points at `ease.out` now and `ease.in` is deleted, so the sentence is
 * history rather than policy and this declaration no longer differs from the others for a
 * reason that has been fixed.
 */
export const moduleNavMotionClosing = "duration-(--motion-duration-disclosure-close)";

/**
 * ONE LEVEL OF THE SIDEBAR'S DRILL, arriving.
 *
 * The product owner asked for the area's modules to be REPLACED by the one you press rather
 * than nesting under it — *"burayı drill menu yapabiliriz"* — so a level slides in from the
 * side it came from while the other goes. It is a NAVIGATION surface run many times a day,
 * which decides every value here: short, and if it reads slow at all the answer is to cut
 * the duration rather than defend it.
 *
 * `--motion-ease-enter` is `ease.out` — a strong curve that moves on its FIRST frame, which
 * is the frame the eye is on. Never `ease.in` here: it is flat for its first fifth, and the
 * product already paid for that once when `--motion-ease-exit` pointed at `ease.in` and
 * every exit in the product started by standing still.
 *
 * # It travels a TOKEN, which is the whole of how reduced motion is honoured
 *
 * `--motion-travel-panel` is re-pointed to `0rem` in the generated stylesheet under
 * `prefers-reduced-motion`, so the levels cross-fade in place and NO component branches on
 * the setting. A literal `translate-x-4` would animate identically and ignore the setting
 * entirely, which is the failure this repository has already met once.
 *
 * ENTERING ONLY, and there is no exiting half deliberately. The two levels are one keyed
 * subtree, so React swaps them rather than holding both — an exit would need both mounted
 * at once, which is a second copy of the list for a frame and a reader's screen reader
 * announcing two navigations. The arriving level carries the whole moment.
 */
export const moduleNavLevel =
  "transition-[translate,opacity] duration-(--motion-duration-disclosure-open) " +
  "ease-(--motion-ease-enter) " +
  "starting:translate-x-(--motion-travel-panel) starting:opacity-0";

/**
 * THE SPACE the sidebar stands in, released only once the panel has finished leaving.
 *
 * The reasoning is in the block above `moduleNavMotion` — this is the half of that moment
 * which is not the panel. `display` rather than `width`: it is discrete, so there is one
 * re-layout rather than one per frame, and `allow-discrete` is what defers it to the end
 * of the exit while applying it at the start of the entrance.
 *
 * IT GOES ON AN EMPTY BOX, and that is measured rather than tidy. Put on the panel's own
 * ancestor — which is the obvious place, since that is the grid item the column is sized
 * by — it fixes the exit and DELETES THE ENTRANCE: opening went from 11 eased painted
 * states to 2, the panel arriving fully drawn at `opacity: 1`. An element revealed out of
 * `display: none` has no previous computed style, so there is nothing for a transition to
 * start from, and the entrance the product owner asked to keep is the one thing lost. The
 * fix for that is `@starting-style`, and taking it would have been the wrong trade twice
 * over: it fires whenever the element is inserted, so the sidebar would also fade in on
 * every navigation INTO a module — the 150ms fade of one column on a screen where every
 * other pixel changed at the same moment, which `frontend/AGENTS.md` refuses by name. So
 * the box that stops being rendered holds nothing, and the panel is never inside it.
 *
 * IT READS THE SAME TOKEN THE PANEL'S EXIT READS, so the space is released on the frame
 * the panel reaches `opacity: 0` and the two cannot drift apart. Two elements animating
 * one moment is exactly the arrangement `overlayScrim` and `panelScrim` are in, and they
 * borrow their panel's duration for the same reason: a second name here would be a
 * licence to drift, and a drift shows as either a gap of empty column or a panel cut off
 * mid-fade.
 *
 * NO CURVE, on purpose. A discrete property has nothing to interpolate, and `display` is
 * specified to hold its non-`none` value for the whole duration regardless — an easing
 * token here would be a value nothing can read.
 *
 * Interruptible, which is why this is a transition and not a timer: re-opening halfway
 * through a close cancels a discrete transition that has not fired yet, so the space is
 * simply never released and the panel retargets from where it is. There is no jump to
 * undo, because nothing had moved.
 */
export const moduleNavSpace =
  "transition-[display] transition-discrete duration-(--motion-duration-disclosure-close)";

/**
 * A CARD RISING UNDER THE POINTER, in a row that is fanned at rest.
 *
 * The product owner asked for the walkthrough cards to fan — *"the middle one on top and
 * the left and right lower. Whichever the user hovers comes to the top"* — with small
 * motion on it.
 *
 * # The lift is the ONLY thing that moves, and that is the design
 *
 * The obvious reading of "comes to the top" is that a hovered side card TRAVELS to the
 * middle's raised slot. It is refused, for the reason `distance.panel` refuses a
 * full-width drawer slide: that journey is the size of `space.fan`, which is a RESTING
 * POSITION and therefore cannot be re-pointed to `0rem` — so under
 * `prefers-reduced-motion: reduce` the one reader who asked for less movement would be
 * handed the largest movement on the screen, or the fan itself would have to be deleted
 * to avoid it. `space.underlap` carries the same argument one component along.
 *
 * So the gesture is expressed as two things that come apart cleanly: the card RISES by
 * `--motion-travel-lift`, which is motion and is re-pointed to zero; and it comes to the
 * FRONT and gains the overlay shadow, which is a state change and survives. Under
 * `reduce` the row still answers the pointer — it simply stops moving to say so.
 *
 * # `translate` and `opacity`, and the shadow is why there are two elements
 *
 * `box-shadow` is a paint property and is not animated here. What transitions is the
 * OPACITY of a shadow layer drawn behind the card, which is compositor work; the card's
 * own surface is opaque, so the layer is invisible except where it spreads past the
 * card's edge, which is the whole of what a raised card's shadow is.
 *
 * # The seat and the lift are different elements on purpose
 *
 * The fan's resting offset is a `--spacing-*` on the grid cell and never transitions —
 * it is where the card LIVES. This declaration goes on the box inside it, which is the
 * only thing that moves. Composing both onto one element would need a `calc` of a space
 * token and a motion token, and the two must not be able to be confused for one value:
 * exactly one of them is re-pointed under `reduce`.
 *
 * The exit duration is the base and the enter duration is the hovered state, which is how
 * a CSS transition expresses an asymmetric pair — and it is a TRANSITION rather than a
 * keyframe for this file's founding reason, sharpened here: a reader crossing a row of
 * cards interrupts this constantly, and a keyframe would restart from the resting
 * position on each crossing where a transition retargets from wherever the card is.
 */
export const raisedCard =
  "relative h-full transition-[translate] " +
  "duration-(--motion-duration-lift-exit) ease-(--motion-ease-enter) " +
  "group-hover/seat:-translate-y-(--motion-travel-lift) " +
  "group-hover/seat:duration-(--motion-duration-lift-enter) " +
  "group-focus-within/seat:-translate-y-(--motion-travel-lift) " +
  "group-focus-within/seat:duration-(--motion-duration-lift-enter)";

/**
 * THE SHADOW UNDER A RAISED CARD, which is the half of the lift that survives `reduce`.
 *
 * Opacity only. It sits BEFORE the card in the document and is `absolute inset-0`, so the
 * card's own opaque surface covers its interior and what shows is the spread past the
 * edge — a real cast shadow rather than a glow drawn over the content.
 *
 * `--shadow-overlay` and not `--shadow-panel`: this is a card that has come forward, and
 * the panel elevation is the one it already sits at. It borrows the lift's durations for
 * `overlayScrim`'s reason — two elements animating one moment must not be able to drift,
 * or the shadow arrives after the card has finished rising.
 */
export const raisedCardShadow =
  "pointer-events-none absolute inset-0 rounded-xl shadow-(--shadow-overlay) opacity-0 " +
  "transition-opacity duration-(--motion-duration-lift-exit) ease-(--motion-ease-enter) " +
  "group-hover/seat:opacity-100 group-hover/seat:duration-(--motion-duration-lift-enter) " +
  "group-focus-within/seat:opacity-100 " +
  "group-focus-within/seat:duration-(--motion-duration-lift-enter)";

/**
 * A SERVER FIGURE GOING STALE while a newer one is fetched, and coming back.
 *
 * The only moment here where nothing enters and nothing leaves: the element is on
 * screen throughout and only its emphasis moves, which is the whole contract in
 * `quoted.tsx` — a number replaced by a spinner makes somebody wait to re-read what
 * they already had.
 *
 * So there is one duration rather than an enter/exit pair, and it is the shortest in
 * the set. A person adjusting a leave period triggers this on every keystroke, and at
 * that frequency an animation is latency paid on each one. `hover` is its neighbour
 * for the same reason.
 */
export const staleFigure =
  "transition-opacity duration-(--motion-duration-stale) ease-(--motion-ease-hover)";

/**
 * A SEARCH FIELD GROWING OUT OF ITS ICON, leftward, and folding back into it.
 *
 * # `width`, and this is the one deviation in the file
 *
 * Everything else here animates `opacity`, `scale` or `translate`, which skip layout and
 * run on the compositor. This animates WIDTH, which does not. The rule is deviate and say
 * so at the point of deviation, so: a control that occupies a different amount of the
 * toolbar open than closed has to change its footprint, and `transform` cannot — a
 * `scaleX` would stretch the glyphs inside it, and a `clip-path` would keep the collapsed
 * field's full width reserved, which is the whole reason it collapses. The cost is one
 * flex row of three items re-laid-out per frame, which is not the case the rule is about;
 * the rule is about lists and large surfaces.
 *
 * # It grows to the LEFT, from the icon, because that is where the space is
 *
 * The icon sits at the trailing edge of the toolbar and stays put. Growing rightward
 * would push the field off the edge or shove the filter button along with it; growing
 * leftward consumes empty space and moves nothing. `justify-end` plus `overflow-hidden`
 * is the whole mechanism — the input is `flex-1` inside a box that changes width, so it
 * is clipped from the left as the box closes rather than re-flowing its text.
 *
 * # Reduced motion removes it rather than softening it
 *
 * The other moments here keep their duration and lose their travel, because a fade is
 * still a fade with nowhere to move. There is no version of this that moves less: the
 * width either animates or it does not. So the token is re-pointed to the instant
 * duration and the field simply IS open.
 */
export const searchMotion =
  "transition-[width,border-color,background-color] " +
  "duration-(--motion-duration-search-open) ease-(--motion-ease-move)";

/** The same, closing. Faster, like every exit here. */
export const searchMotionClosing = "duration-(--motion-duration-search-close)";

/**
 * NOTHING, on purpose, and written down so it survives somebody tidying up.
 *
 * The command palette is opened by a keystroke, dozens to hundreds of times a day, and
 * is the deliberate shortest path to every destination in the product. An animation in
 * front of it is latency paid on every single invocation and skippable on none. Raycast
 * ships its palette with no open animation for exactly this reason.
 *
 * Riksu already reads this way — by the dead-class bug rather than by decision, which
 * means it would regress the moment somebody made the animations work. This is the
 * decision, with its reason attached.
 */
export const instant = "transition-none";
